import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledFormSection from '../styled/StyledFormSection';
import { Label } from '../ui/label';
import StyledInput from '../styled/StyledInput';
import StyledButton from '../styled/StyledButton';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useTranslation } from '@/i18n';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface CircuitParticipantsSectionProps {
  control: Control<CircuitFormDataWithTranslations>;
  isViewMode?: boolean;
}

const CircuitParticipantsSection = ({
  control,
  isViewMode = false,
}: CircuitParticipantsSectionProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'childAges',
  });

  const firstChildAgeTo = useWatch({ control, name: 'childAges.0.childAgeTo' });

  const { setValue } = useFormContext<CircuitFormDataWithTranslations>();

  return (
    <StyledFormSection sectionTitle={t('circuits.participantsSection.title')}>
      <Label className="text-gray-700">
        {t('circuits.participantsSection.description')}
      </Label>
      <div className="flex items-center gap-2">
        <Label className="text-gray-700 w-16">
          {t('circuits.participantsSection.adults')}
        </Label>
        <div className="w-20">
          <StyledInput
            control={control}
            name="nbParticipants"
            type="number"
            min={0}
            disabled={isViewMode}
          />
        </div>
      </div>
      <CircuitChildParticipantBlock
        control={control}
        index={0}
        isViewMode={isViewMode}
      />
      {fields.length > 1 ? (
        <div className="flex items-center justify-between flex-wrap">
          <CircuitChildParticipantBlock
            control={control}
            index={1}
            isViewMode={isViewMode}
          />
          <StyledButton
            variant="link"
            className="p-0 w-fit"
            title={t('circuits.participantsSection.delete')}
            onClick={() => {
              remove(1);
              setValue('nbAdolescents', 0);
            }}
            disabled={isViewMode}
          />
        </div>
      ) : (
        <StyledButton
          variant="link"
          title={t('circuits.participantsSection.addRange')}
          className="w-fit p-0 text-brand-700"
          icon={<PlusIcon className="size-5 fill-brand-700" />}
          onClick={() =>
            append({
              childAgeFrom: Number(firstChildAgeTo ?? 11) + 1,
              childAgeTo:
                Number(firstChildAgeTo ?? 11) > 17
                  ? Number(firstChildAgeTo ?? 11) + 2
                  : 17,
            })
          }
          disabled={isViewMode}
        />
      )}
    </StyledFormSection>
  );
};

export default CircuitParticipantsSection;

interface CircuitChildParticipantBlockProps {
  control: Control<CircuitFormDataWithTranslations>;
  index: number;
  isViewMode?: boolean;
}

const CircuitChildParticipantBlock = ({
  control,
  index,
  isViewMode = false,
}: CircuitChildParticipantBlockProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Label className="text-gray-700 w-16">
        {t('circuits.participantsSection.children')}
      </Label>
      <div className="w-20">
        <StyledInput
          control={control}
          name={index === 0 ? 'nbChildren' : 'nbAdolescents'}
          type="number"
          min={0}
          disabled={isViewMode}
        />
      </div>
      <Label className="text-gray-700 w-6">
        {t('circuits.participantsSection.from')}
      </Label>
      <div className="w-20">
        <StyledInput
          control={control}
          name={`childAges.${index}.childAgeFrom`}
          type="number"
          min={1}
          disabled={isViewMode}
        />
      </div>
      <Label className="text-gray-700 w-12">
        {t('circuits.participantsSection.yearsTo')}
      </Label>
      <div className="w-20">
        <StyledInput
          control={control}
          name={`childAges.${index}.childAgeTo`}
          type="number"
          min={2}
          disabled={isViewMode}
        />
      </div>
      <Label className="text-gray-700 w-20">
        {t('circuits.participantsSection.yearsIncluded')}
      </Label>
    </div>
  );
};
